import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
  Alert,
  Snackbar,
  useTheme
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dashboardsService from '../services/DashboardsService';
import { logger } from 'utils/logger';

const log = logger.getLogger('SiteDowntimeSection');

/** Convert an ISO 8601 string to the value format expected by a datetime-local input (YYYY-MM-DDTHH:mm). */
function isoToDatetimeLocal(iso: string): string {
  const date = new Date(iso);
  // toISOString() gives "YYYY-MM-DDTHH:mm:ss.sssZ"; slice to "YYYY-MM-DDTHH:mm"
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

const SiteDowntimeSection: React.FC = () => {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // The datetime-local input value (YYYY-MM-DDTHH:mm), or '' when unset
  const [datetimeValue, setDatetimeValue] = useState('');
  // The last-saved value — used to detect changes and to populate the confirm dialog
  const [savedValue, setSavedValue] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load current site downtime on mount
  useEffect(() => {
    const fetch = async () => {
      try {
        const iso = await dashboardsService.getSiteDowntime();
        if (iso) {
          const local = isoToDatetimeLocal(iso);
          setDatetimeValue(local);
          setSavedValue(local);
        } else {
          setDatetimeValue('');
          setSavedValue(null);
        }
      } catch (err) {
        log.error('Failed to fetch site downtime:', err);
        setError('Failed to load site downtime.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleSet = () => {
    setError(null);
    setConfirmOpen(true);
  };

  const handleConfirmSet = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      // Pass the local datetime string; service converts to ISO
      await dashboardsService.setSiteDowntime(datetimeValue);
      setSavedValue(datetimeValue);
      showSuccess('Site downtime set successfully.');
    } catch (err) {
      log.error('Failed to set site downtime:', err);
      setError('Failed to set site downtime. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await dashboardsService.setSiteDowntime(null);
      setDatetimeValue('');
      setSavedValue(null);
      showSuccess('Site downtime cleared.');
    } catch (err) {
      log.error('Failed to clear site downtime:', err);
      setError('Failed to clear site downtime. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isDirty = datetimeValue !== (savedValue ?? '');
  const hasValue = !!datetimeValue;
  const hasSavedValue = !!savedValue;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 3,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: (t) =>
            t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}
        >
          Site Downtime
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Setting a site downtime will lock down Strategy Edge at the specified date and time.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Downtime Date &amp; Time"
            type="datetime-local"
            value={datetimeValue}
            onChange={(e) => setDatetimeValue(e.target.value)}
            disabled={loading || submitting}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 260 }}
          />

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', pt: 0.5 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSet}
              disabled={loading || submitting || !hasValue || !isDirty}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': { boxShadow: 4 }
              }}
            >
              Set Site Downtime
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={handleClear}
              disabled={loading || submitting || !hasSavedValue}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Clear Site Downtime
            </Button>
          </Box>
        </Box>

      </Paper>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Set Site Downtime
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will override any other downtime for other dashboards, and will lockdown Strategy
            Edge at the given time. Are you sure you want to do this?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmSet}
            variant="contained"
            color="primary"
            sx={{ fontWeight: 600 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SiteDowntimeSection;
